import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCachedMembershipSnapshot, setCachedMembershipSnapshot } from '@vaults/ui/management/ClientCacheStore';

export type RoleValue = 'viewer' | 'contributor' | 'editor' | 'manager' | 'admin' | 'owner';

type MembershipStatus = 'active' | 'pending';

type AllowlistScope = 'ORG' | 'VAULT';

interface MembershipUser {
  id: string;
  name?: string | null;
  email: string;
  image?: string | null;
}

interface VaultReference {
  id: string;
  title: string;
  slug: string;
}

interface OrganizationReference {
  id: string;
  name: string;
  slug: string;
}

export interface MembershipEntry {
  id: string;
  type: 'membership' | 'invite';
  status: MembershipStatus;
  email: string;
  role: RoleValue;
  user: MembershipUser | null;
  vault: VaultReference | null;
  organization: OrganizationReference | null;
  invitedBy?: {
    id: string;
    name?: string | null;
    email: string;
  } | null;
  createdAt?: string;
}

export interface AllowlistEntry {
  id: string;
  value: string;
  scope: AllowlistScope;
  organization: OrganizationReference | null;
  vault: VaultReference | null;
  createdAt?: string;
}

interface UseVaultAccessManagementOptions {
  vaultId?: string | null;
  organizationId?: string | null;
}

interface UseVaultAccessManagementResult {
  loading: boolean;
  error: string | null;
  entries: MembershipEntry[];
  members: MembershipEntry[];
  invites: MembershipEntry[];
  allowlist: AllowlistEntry[];
  organizationAllowlist: AllowlistEntry[];
  memberCount: number;
  inviteMember: (params: { email: string; role: RoleValue }) => Promise<void>;
  updateMemberRole: (params: { entryId: string; role: RoleValue }) => Promise<void>;
  removeMembershipEntry: (entryId: string) => Promise<void>;
  addAllowlistDomain: (params: { domain: string; scope?: AllowlistScope }) => Promise<void>;
  removeAllowlistEntry: (entryId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

async function readError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.error === 'string') {
      return data.error;
    }
    if (typeof data?.message === 'string') {
      return data.message;
    }
  } catch (err) {
    // Ignore JSON parse errors and fall back to text.
  }

  try {
    const text = await response.text();
    if (text) {
      return text;
    }
  } catch (err) {
    // Ignore text parse errors.
  }

  return `Request failed with status ${response.status}`;
}

export function useVaultAccessManagement({
  vaultId,
  organizationId,
}: UseVaultAccessManagementOptions): UseVaultAccessManagementResult {
  const [entries, setEntries] = useState<MembershipEntry[]>([]);
  const [allowlist, setAllowlist] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!vaultId && !organizationId) {
      setEntries([]);
      setAllowlist([]);
      setError(null);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/memberships', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(await readError(response));
      }
      const payload = await response.json();
      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setAllowlist(Array.isArray(payload.allowlists) ? payload.allowlists : []);
      setCachedMembershipSnapshot(vaultId ?? undefined, organizationId ?? undefined, {
        entries: Array.isArray(payload.entries) ? payload.entries : [],
        allowlists: Array.isArray(payload.allowlists) ? payload.allowlists : [],
      });
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load access data.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [vaultId, organizationId]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!vaultId) {
        setEntries([]);
        setAllowlist([]);
        setError(null);
        return;
      }
      // Seed from cache to avoid flicker on tab switches
      const cached = getCachedMembershipSnapshot(vaultId, organizationId);
      if (cached) {
        setEntries(Array.isArray(cached.entries) ? cached.entries : []);
        setAllowlist(Array.isArray(cached.allowlists) ? cached.allowlists : []);
      }
      await refresh();
      if (!active) {
        return;
      }
    })();

    return () => {
      active = false;
    };
  }, [vaultId, organizationId, refresh]);

  const members = useMemo(
    () => entries.filter((entry) => entry.type === 'membership' && entry.vault?.id === vaultId),
    [entries, vaultId],
  );

  const invites = useMemo(
    () => entries.filter((entry) => entry.type === 'invite' && entry.vault?.id === vaultId),
    [entries, vaultId],
  );

  const vaultAllowlist = useMemo(
    () => allowlist.filter((entry) => entry.scope === 'VAULT' && entry.vault?.id === vaultId),
    [allowlist, vaultId],
  );

  const orgAllowlist = useMemo(
    () =>
      allowlist.filter(
        (entry) => entry.scope === 'ORG' && entry.organization?.id && entry.organization.id === organizationId,
      ),
    [allowlist, organizationId],
  );

  const memberCount = members.length;

  const inviteMember = useCallback(
    async ({ email, role }: { email: string; role: RoleValue }) => {
      if (!vaultId) {
        throw new Error('A vault must be selected before inviting members.');
      }

      const response = await fetch('/api/memberships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role, vaultId }),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const payload = await response.json();
      if (payload?.entry) {
        setEntries((previous) => {
          const next = previous.filter((entry) => entry.id !== payload.entry.id);
          next.unshift(payload.entry as MembershipEntry);
          // update cache with optimistic state
          setCachedMembershipSnapshot(vaultId, organizationId, {
            entries: next,
            allowlists: allowlist,
          });
          return next;
        });
      } else {
        await refresh();
      }
    },
    [vaultId, organizationId, allowlist, refresh],
  );

  const updateMemberRole = useCallback(
    async ({ entryId, role }: { entryId: string; role: RoleValue }) => {
      const response = await fetch(`/api/memberships/${entryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const payload = await response.json();
      if (payload?.entry) {
        setEntries((previous) => {
          const next = previous.map((entry) => (entry.id === payload.entry.id ? payload.entry : entry)) as MembershipEntry[];
          setCachedMembershipSnapshot(vaultId, organizationId, {
            entries: next,
            allowlists: allowlist,
          });
          return next;
        });
      } else {
        await refresh();
      }
    },
    [vaultId, organizationId, allowlist, refresh],
  );

  const removeMembershipEntry = useCallback(
    async (entryId: string) => {
      const response = await fetch(`/api/memberships/${entryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      setEntries((previous) => {
        const next = previous.filter((entry) => entry.id !== entryId);
        setCachedMembershipSnapshot(vaultId, organizationId, {
          entries: next,
          allowlists: allowlist,
        });
        return next;
      });
    },
    [vaultId, organizationId, allowlist],
  );

  const addAllowlistDomain = useCallback(
    async ({ domain, scope = 'VAULT' }: { domain: string; scope?: AllowlistScope }) => {
      if (scope === 'VAULT' && !vaultId) {
        throw new Error('A vault must be selected before adding an allowlist domain.');
      }

      const response = await fetch('/api/domain-allowlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          value: domain,
          scope: scope.toLowerCase(),
          vaultId: scope === 'VAULT' ? vaultId : undefined,
          organizationId: scope === 'ORG' ? organizationId : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const payload = await response.json();
      if (payload?.entry) {
        setAllowlist((previous) => {
          const next = [...previous, payload.entry as AllowlistEntry];
          setCachedMembershipSnapshot(vaultId, organizationId, {
            entries,
            allowlists: next,
          });
          return next;
        });
      } else {
        await refresh();
      }
    },
    [vaultId, organizationId, entries, refresh],
  );

  const removeAllowlistEntry = useCallback(async (entryId: string) => {
    const response = await fetch(`/api/domain-allowlist/${entryId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(await readError(response));
    }

    setAllowlist((previous) => {
      const next = previous.filter((entry) => entry.id !== entryId);
      setCachedMembershipSnapshot(vaultId, organizationId, {
        entries,
        allowlists: next,
      });
      return next;
    });
  }, [vaultId, organizationId, entries]);

  return {
    loading,
    error,
    entries,
    members,
    invites,
    allowlist: vaultAllowlist,
    organizationAllowlist: orgAllowlist,
    memberCount,
    inviteMember,
    updateMemberRole,
    removeMembershipEntry,
    addAllowlistDomain,
    removeAllowlistEntry,
    refresh,
  };
}
